import React, { useState, useEffect } from 'react';
import { Upload, Download, FileJson, AlertCircle, CheckCircle, Loader2, FileUp, Database, Link, RefreshCw, Plus, Trash2, ExternalLink, FileSpreadsheet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImportExportPage() {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  
  // Export format toggle
  const [exportFormat, setExportFormat] = useState('json'); // 'json' or 'csv'
  
  // URL Import state
  const [urlInput, setUrlInput] = useState('');
  const [urlDataType, setUrlDataType] = useState('auto');
  const [urlPreview, setUrlPreview] = useState(null);
  const [urlLoading, setUrlLoading] = useState(false);
  
  // Saved URL Sources state
  const [urlSources, setUrlSources] = useState([]);
  const [showAddSource, setShowAddSource] = useState(false);
  const [newSource, setNewSource] = useState({ name: '', url: '', data_type: 'auto' });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Load saved URL sources on mount
  useEffect(() => {
    loadUrlSources();
  }, []);

  const loadUrlSources = async () => {
    try {
      const response = await fetch(`${API_URL}/api/configurator-settings/url-sources`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUrlSources(data);
      }
    } catch (error) {
      console.error('Failed to load URL sources:', error);
    }
  };

  // Export Functions
  const handleExportAll = async () => {
    setExporting(true);
    try {
      if (exportFormat === 'csv') {
        // Download ZIP file with all CSVs
        const response = await fetch(`${API_URL}/api/configurator-settings/export/csv/all`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (!response.ok) throw new Error('Export failed');
        
        const blob = await response.blob();
        downloadBlob(blob, 'fusion-export.zip');
        toast.success('CSV export completed successfully');
      } else {
        const response = await fetch(`${API_URL}/api/configurator-settings/export`, {
          headers: getAuthHeaders()
        });
        
        if (!response.ok) throw new Error('Export failed');
        
        const data = await response.json();
        downloadJSON(data, 'fusion-full-export.json');
        toast.success('JSON export completed successfully');
      }
    } catch (error) {
      toast.error('Export failed: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleExportConfigurator = async () => {
    setExporting(true);
    try {
      if (exportFormat === 'csv') {
        // For CSV, we'll download individual files
        toast.info('Downloading individual CSV files...');
        await downloadCSV('colors');
        await downloadCSV('doors');
        await downloadCSV('windows');
        await downloadCSV('styles');
        await downloadCSV('services');
        toast.success('Configurator CSVs downloaded');
      } else {
        const response = await fetch(`${API_URL}/api/configurator-settings/export/configurator`, {
          headers: getAuthHeaders()
        });
        
        if (!response.ok) throw new Error('Export failed');
        
        const data = await response.json();
        downloadJSON(data, 'fusion-configurator-settings.json');
        toast.success('Configurator settings exported successfully');
      }
    } catch (error) {
      toast.error('Export failed: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPricing = async () => {
    setExporting(true);
    try {
      if (exportFormat === 'csv') {
        await downloadCSV('pricing');
        toast.success('Pricing CSV downloaded');
      } else {
        const response = await fetch(`${API_URL}/api/configurator-settings/export/pricing`, {
          headers: getAuthHeaders()
        });
        
        if (!response.ok) throw new Error('Export failed');
        
        const data = await response.json();
        downloadJSON(data, 'fusion-pricing-data.json');
        toast.success('Pricing data exported successfully');
      }
    } catch (error) {
      toast.error('Export failed: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const downloadCSV = async (type) => {
    const response = await fetch(`${API_URL}/api/configurator-settings/export/csv/${type}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    
    if (!response.ok) throw new Error(`Failed to download ${type} CSV`);
    
    const blob = await response.blob();
    downloadBlob(blob, `fusion-${type}.csv`);
  };

  const downloadJSON = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    downloadBlob(blob, filename);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Individual CSV exports
  const handleExportIndividualCSV = async (type) => {
    setExporting(true);
    try {
      await downloadCSV(type);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} CSV downloaded`);
    } catch (error) {
      toast.error('Export failed: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  // File Import Functions
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setValidationResult(null);
    setPreviewData(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      setPreviewData(data);
      
      await validateImport(data);
    } catch (error) {
      toast.error('Invalid JSON file: ' + error.message);
      setSelectedFile(null);
    }
  };

  const validateImport = async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/configurator-settings/import/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      setValidationResult(result);
      
      if (result.valid) {
        toast.success('File validated successfully');
      } else {
        toast.warning('File has validation issues');
      }
    } catch (error) {
      toast.error('Validation failed: ' + error.message);
    }
  };

  const handleImport = async () => {
    if (!previewData) {
      toast.error('No file selected');
      return;
    }

    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/configurator-settings/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(previewData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Import failed');
      }
      
      toast.success('Import completed successfully');
      setSelectedFile(null);
      setPreviewData(null);
      setValidationResult(null);
      
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      toast.error('Import failed: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  // URL Import Functions
  const handleUrlPreview = async () => {
    if (!urlInput.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    setUrlLoading(true);
    setUrlPreview(null);

    try {
      const response = await fetch(`${API_URL}/api/configurator-settings/import/url/preview`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          url: urlInput,
          data_type: urlDataType
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Preview failed');
      }
      
      const preview = await response.json();
      setUrlPreview(preview);
      toast.success('URL data fetched successfully');
    } catch (error) {
      toast.error('Failed to fetch URL: ' + error.message);
    } finally {
      setUrlLoading(false);
    }
  };

  const handleUrlImport = async () => {
    if (!urlInput.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    setUrlLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/configurator-settings/import/url`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          url: urlInput,
          data_type: urlDataType
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Import failed');
      }
      
      const result = await response.json();
      toast.success('URL import completed successfully');
      setUrlInput('');
      setUrlPreview(null);
    } catch (error) {
      toast.error('URL import failed: ' + error.message);
    } finally {
      setUrlLoading(false);
    }
  };

  // URL Source Functions
  const handleAddSource = async () => {
    if (!newSource.name || !newSource.url) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/configurator-settings/url-sources`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          id: `source_${Date.now()}`,
          ...newSource,
          active: true
        })
      });
      
      if (!response.ok) throw new Error('Failed to add source');
      
      toast.success('URL source added successfully');
      setNewSource({ name: '', url: '', data_type: 'auto' });
      setShowAddSource(false);
      loadUrlSources();
    } catch (error) {
      toast.error('Failed to add source: ' + error.message);
    }
  };

  const handleSyncSource = async (sourceId) => {
    try {
      const response = await fetch(`${API_URL}/api/configurator-settings/url-sources/${sourceId}/sync`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Sync failed');
      }
      
      toast.success('Data synced successfully');
      loadUrlSources();
    } catch (error) {
      toast.error('Sync failed: ' + error.message);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm('Are you sure you want to delete this URL source?')) return;

    try {
      const response = await fetch(`${API_URL}/api/configurator-settings/url-sources/${sourceId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to delete source');
      
      toast.success('URL source deleted');
      loadUrlSources();
    } catch (error) {
      toast.error('Failed to delete source: ' + error.message);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="import-export-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Import / Export Data</h1>
        <p className="text-gray-600 mt-2">
          Export, import, or sync configurator settings and pricing data from files or URLs.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Export Section */}
        <Card data-testid="export-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-600" />
              Export Data
            </CardTitle>
            <CardDescription>
              Download your configuration data as JSON or CSV files.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Format Toggle */}
            <div className="flex items-center justify-center gap-2 p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <button
                onClick={() => setExportFormat('json')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  exportFormat === 'json' 
                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <FileJson className="h-4 w-4" />
                JSON
              </button>
              <button
                onClick={() => setExportFormat('csv')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  exportFormat === 'csv' 
                    ? 'bg-white dark:bg-gray-700 text-green-600 dark:text-green-400 shadow-sm' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <FileSpreadsheet className="h-4 w-4" />
                CSV
              </button>
            </div>

            <Button
              onClick={handleExportAll}
              disabled={exporting}
              className={`w-full ${exportFormat === 'csv' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              data-testid="export-all-btn"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Database className="h-4 w-4 mr-2" />
              )}
              Export All ({exportFormat === 'csv' ? 'ZIP' : 'JSON'})
            </Button>
            
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={handleExportConfigurator} disabled={exporting} variant="outline" size="sm">
                {exportFormat === 'csv' ? <FileSpreadsheet className="h-4 w-4 mr-1" /> : <FileJson className="h-4 w-4 mr-1" />}
                Config
              </Button>
              <Button onClick={handleExportPricing} disabled={exporting} variant="outline" size="sm">
                {exportFormat === 'csv' ? <FileSpreadsheet className="h-4 w-4 mr-1" /> : <FileJson className="h-4 w-4 mr-1" />}
                Pricing
              </Button>
            </div>

            {/* Individual CSV Downloads */}
            {exportFormat === 'csv' && (
              <div className="pt-2 border-t">
                <p className="text-xs text-gray-500 mb-2">Individual CSV exports:</p>
                <div className="grid grid-cols-3 gap-2">
                  {['colors', 'doors', 'windows', 'styles', 'services'].map(type => (
                    <Button 
                      key={type}
                      onClick={() => handleExportIndividualCSV(type)} 
                      disabled={exporting} 
                      variant="ghost" 
                      size="sm"
                      className="text-xs"
                    >
                      {type}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h4 className="font-medium text-xs text-gray-700 dark:text-gray-200 mb-2">Export includes:</h4>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                <li>• Colors, Doors, Windows</li>
                <li>• Building styles & defaults</li>
                <li>• Services, Pricing, Lean-to dims</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* File Import Section */}
        <Card data-testid="import-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-green-600" />
              Import from File
            </CardTitle>
            <CardDescription>
              Upload a JSON file to restore configuration data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
              <input
                type="file"
                id="file-upload"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <FileUp className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {selectedFile ? selectedFile.name : 'Click to select JSON'}
                </p>
              </label>
            </div>

            {validationResult && (
              <div className={`p-3 rounded-lg text-sm ${validationResult.valid ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {validationResult.valid ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                  )}
                  <span className={`font-medium ${validationResult.valid ? 'text-green-700' : 'text-yellow-700'}`}>
                    {validationResult.valid ? 'Valid' : 'Issues Found'}
                  </span>
                </div>
                {validationResult.preview?.configurator_settings && (
                  <div className="text-xs text-gray-600 mt-2">
                    {Object.entries(validationResult.preview.configurator_settings).map(([k, v]) => (
                      <span key={k} className="mr-2">{k}: {v}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Button
              onClick={handleImport}
              disabled={importing || !previewData || (validationResult && !validationResult.valid)}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Import Data
            </Button>
          </CardContent>
        </Card>

        {/* URL Import Section */}
        <Card data-testid="url-import-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link className="h-5 w-5 text-purple-600" />
              Import from URL
            </CardTitle>
            <CardDescription>
              Fetch and import data directly from an external URL.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Input
                placeholder="https://example.com/data.json"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                className="text-sm"
              />
              <select
                value={urlDataType}
                onChange={(e) => setUrlDataType(e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
              >
                <option value="auto">Auto-detect type</option>
                <option value="all">Full Export</option>
                <option value="colors">Colors only</option>
                <option value="doors">Doors only</option>
                <option value="windows">Windows only</option>
                <option value="styles">Styles only</option>
                <option value="pricing">Pricing only</option>
              </select>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleUrlPreview} disabled={urlLoading || !urlInput} variant="outline" className="flex-1" size="sm">
                {urlLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-1" />}
                Preview
              </Button>
              <Button onClick={handleUrlImport} disabled={urlLoading || !urlInput} className="flex-1 bg-purple-600 hover:bg-purple-700" size="sm">
                <Link className="h-4 w-4 mr-1" />
                Import
              </Button>
            </div>

            {urlPreview && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                <p className="font-medium text-purple-700 mb-1">Preview:</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">Type: {urlPreview.detected_type}</p>
                {urlPreview.item_counts && Object.keys(urlPreview.item_counts).length > 0 && (
                  <div className="text-xs text-gray-600 mt-1">
                    {Object.entries(urlPreview.item_counts).map(([k, v]) => (
                      <span key={k} className="mr-2">{k}: {v}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Saved URL Sources */}
      <Card className="mt-6" data-testid="url-sources-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-indigo-600" />
              Saved URL Data Sources
            </CardTitle>
            <CardDescription>
              Save and manage external data sources for quick syncing.
            </CardDescription>
          </div>
          <Button onClick={() => setShowAddSource(!showAddSource)} variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Add Source
          </Button>
        </CardHeader>
        <CardContent>
          {showAddSource && (
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border dark:border-gray-700">
              <h4 className="font-medium text-sm mb-3 text-gray-900 dark:text-white">Add New URL Source</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input
                  placeholder="Source Name"
                  value={newSource.name}
                  onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                />
                <Input
                  placeholder="URL"
                  value={newSource.url}
                  onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
                  className="md:col-span-2"
                />
                <select
                  value={newSource.data_type}
                  onChange={(e) => setNewSource({ ...newSource, data_type: e.target.value })}
                  className="px-3 py-2 text-sm border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                >
                  <option value="auto">Auto-detect</option>
                  <option value="all">Full Export</option>
                  <option value="colors">Colors</option>
                  <option value="doors">Doors</option>
                  <option value="windows">Windows</option>
                  <option value="styles">Styles</option>
                  <option value="pricing">Pricing</option>
                </select>
              </div>
              <div className="flex gap-2 mt-3">
                <Button onClick={handleAddSource} size="sm">Save Source</Button>
                <Button onClick={() => setShowAddSource(false)} variant="outline" size="sm">Cancel</Button>
              </div>
            </div>
          )}

          {urlSources.length > 0 ? (
            <div className="space-y-2">
              {urlSources.map((source) => (
                <div key={source.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <div className="flex-1">
                    <p className="font-medium text-sm text-gray-900 dark:text-white">{source.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">{source.url}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Type: {source.data_type} 
                      {source.last_sync && ` • Last sync: ${new Date(source.last_sync).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleSyncSource(source.id)} size="sm" variant="outline">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button onClick={() => handleDeleteSource(source.id)} size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">
              No saved URL sources. Add one to enable quick data syncing.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Data Preview */}
      {(previewData || urlPreview?.transformed_data) && (
        <Card className="mt-6" data-testid="preview-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileJson className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              Data Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-64 text-xs">
              {JSON.stringify(previewData || urlPreview?.transformed_data, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
